import { Component, type ErrorInfo, type ReactNode } from 'react'
import { emit } from './events'

interface Props {
  name: string
  children: ReactNode
  fallback?: ReactNode
}

interface State {
  failed: boolean
}

/**
 * Wraps every mounted component.
 *
 * This is load-bearing rather than defensive polish: these widgets run inside
 * the marketing site, and a render crash that escaped would take out whatever
 * else is on the page. On failure we render nothing by default — an empty slot
 * degrades to the static Webflow layout, which is a far better outcome than a
 * broken box in the middle of the homepage.
 */
export class ComponentBoundary extends Component<Props, State> {
  state: State = { failed: false }

  static getDerivedStateFromError(): State {
    return { failed: true }
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error(`[codeverse] component "${this.props.name}" crashed`, error, info.componentStack)
    emit('component:error', { name: this.props.name, error })
  }

  render(): ReactNode {
    if (this.state.failed) return this.props.fallback ?? null
    return this.props.children
  }
}
